import React from 'react';
import { TRANSPORT_SERVICE_OPTIONS } from '../../constants/transportServiceOptions';
import { isPlQtyActive } from './shipmentPlanFieldUtils';

const fieldClassName =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500';

export const ShipmentPlanDetailFields = ({
  row,
  draft,
  onDraftChange,
  onFieldBlur,
  loadingTotalWeight = false,
  shipmentDestinationTotalWeight = null,
}) => {
  const plQtyDisabled =
    row.pl_qty_master === null || row.pl_qty_master === undefined;
  const packageQtyDisabled = isPlQtyActive(draft);

  const handleChange = (field) => (e) => {
    onDraftChange(field, e.target.value);
  };

  const handleBlur = (field) => () => {
    onFieldBlur(field);
  };

  const handlePlQtyChange = (e) => {
    const { value } = e.target;
    onDraftChange('pl_qty', value === '' ? '' : value);
  };

  const handleTrackingChange = (e) => {
    const { value } = e.target;
    if (value === '' || /^\d+$/.test(value)) {
      onDraftChange('tracking_number', value);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">発送日（発注明細レベル）</label>
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="date"
            value={draft.shipment_date}
            onChange={handleChange('shipment_date')}
            onBlur={handleBlur('shipment_date')}
            className={`flex-1 ${fieldClassName}`}
          />
          <span className="text-slate-600 text-sm whitespace-nowrap">
            発送先総重量:{' '}
            {loadingTotalWeight ? (
              <span className="text-slate-400">(計算中...)</span>
            ) : shipmentDestinationTotalWeight !== null ? (
              `${shipmentDestinationTotalWeight.toFixed(2)} kg`
            ) : row.total_weight !== null && row.total_weight !== undefined ? (
              `${row.total_weight.toFixed(2)} kg`
            ) : (
              '-'
            )}
          </span>
        </div>
        {row.split_plans && row.split_plans.length > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            ※ 分納計画がある場合は、分納計画の日付が優先表示されます
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">調整メモ</label>
        <textarea
          value={draft.adjustment_memo}
          onChange={handleChange('adjustment_memo')}
          onBlur={handleBlur('adjustment_memo')}
          className={`${fieldClassName} resize-y min-h-[100px]`}
          placeholder="調整メモを入力してください"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">発送準備</label>
        <select
          value={draft.shipping_preparation}
          onChange={handleChange('shipping_preparation')}
          onBlur={handleBlur('shipping_preparation')}
          className={fieldClassName}
        >
          <option value="">選択してください</option>
          <option value="済">済</option>
          <option value="入荷">入荷</option>
          <option value="工場在庫">工場在庫</option>
          <option value="メーカー直送">メーカー直送</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">PL数（マスタ）</label>
          <div className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-900">
            {row.pl_qty_master !== null && row.pl_qty_master !== undefined ? row.pl_qty_master : '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">PL数</label>
          <input
            type="number"
            value={draft.pl_qty}
            onChange={handlePlQtyChange}
            onBlur={handleBlur('pl_qty')}
            min="0"
            max={row.pl_qty_master || undefined}
            disabled={plQtyDisabled}
            className={fieldClassName}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">個口数量</label>
        <select
          value={draft.package_qty || ''}
          onChange={handleChange('package_qty')}
          onBlur={handleBlur('package_qty')}
          disabled={packageQtyDisabled}
          className={fieldClassName}
        >
          <option value="">選択してください</option>
          <option value="同梱">同梱</option>
          <option value="1個">1個</option>
          <option value="2個">2個</option>
          <option value="3個">3個</option>
          <option value="4個">4個</option>
          <option value="5個">5個</option>
          <option value="ミニ">ミニ</option>
          <option value="才">才</option>
          <option value="確認中">確認中</option>
          <option value="引取">引取</option>
          <option value="製品と一緒に">製品と一緒に</option>
          <option value="蓋をしないで">蓋をしないで</option>
          <option value="営業便">営業便</option>
          <option value="重量計さん">重量計さん</option>
          <option value="7才">7才</option>
          <option value="47才">47才</option>
          <option value="72才">72才</option>
          <option value="型枠と一緒に">型枠と一緒に</option>
        </select>
        {packageQtyDisabled && (
          <p className="text-xs text-slate-500 mt-1">※ PL数が入力されている場合は無効化されます</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">発送希望日</label>
          <input
            type="date"
            value={draft.desired_shipment_date}
            onChange={handleChange('desired_shipment_date')}
            onBlur={handleBlur('desired_shipment_date')}
            className={fieldClassName}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">着希望日</label>
          <input
            type="date"
            value={draft.desired_arrival_date}
            onChange={handleChange('desired_arrival_date')}
            onBlur={handleBlur('desired_arrival_date')}
            className={fieldClassName}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">コメント</label>
        <textarea
          value={draft.comment}
          onChange={handleChange('comment')}
          onBlur={handleBlur('comment')}
          className={`${fieldClassName} resize-y min-h-[100px]`}
          placeholder="コメントを入力してください"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">受注チェック</label>
          <select
            value={draft.order_check}
            onChange={handleChange('order_check')}
            onBlur={handleBlur('order_check')}
            className={fieldClassName}
          >
            <option value="">選択してください</option>
            <option value="済">済</option>
            <option value="未">未</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Wチェック</label>
          <select
            value={draft.w_check}
            onChange={handleChange('w_check')}
            onBlur={handleBlur('w_check')}
            className={fieldClassName}
          >
            <option value="">選択してください</option>
            <option value="工場出荷">工場出荷</option>
            <option value="チェック済">チェック済</option>
            <option value="宝機材製に変更案内文OK">宝機材製に変更案内文OK</option>
            <option value="工場渡し">工場渡し</option>
            <option value="トラック便">トラック便</option>
            <option value="発送ストップ">発送ストップ</option>
            <option value="営業便">営業便</option>
            <option value="資材倉庫渡し">資材倉庫渡し</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">キャンセル・返品・不具合</label>
          <select
            value={draft.cancel_return_issue}
            onChange={handleChange('cancel_return_issue')}
            onBlur={handleBlur('cancel_return_issue')}
            className={fieldClassName}
          >
            <option value="">選択してください</option>
            <option value="キャンセル">キャンセル</option>
            <option value="返品">返品</option>
            <option value="交換">交換</option>
            <option value="不具合が発生">不具合が発生</option>
            <option value="副愛品の代替品">副愛品の代替品</option>
            <option value="残貨">残貨</option>
            <option value="サンプル">サンプル</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">送料メモ</label>
        <textarea
          value={draft.shipping_memo}
          onChange={handleChange('shipping_memo')}
          onBlur={handleBlur('shipping_memo')}
          className={`${fieldClassName} resize-y min-h-[100px]`}
          placeholder="送料条件や発送後の対応メモなどを入力してください"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">運送便</label>
          <select
            value={draft.transport_service}
            onChange={handleChange('transport_service')}
            onBlur={handleBlur('transport_service')}
            className={fieldClassName}
          >
            <option value="">選択してください</option>
            {TRANSPORT_SERVICE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">送り状No</label>
          <input
            type="text"
            value={draft.tracking_number}
            onChange={handleTrackingChange}
            onBlur={handleBlur('tracking_number')}
            className={fieldClassName}
            placeholder="送り状Noを入力してください（数値のみ）"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">総重量</label>
          <div className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-900">
            {row.total_weight !== null && row.total_weight !== undefined
              ? `${row.total_weight.toFixed(2)} kg`
              : '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">着日</label>
          <div className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-900">
            {row.arrival_date ? new Date(row.arrival_date).toISOString().split('T')[0] : '-'}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">得意先コード</label>
        <div className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-900">
          {row.customer_disp || row.customer_code || '-'}
        </div>
      </div>
    </div>
  );
};
